import React, { useEffect, useState } from 'react';
import { addHours } from 'date-fns';
import Link from '@dashboard/components/Link';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import Callout from '@dashboard/components/Callout';
import { formatPrice } from '@dashboard/services/formatting';
import MultilineText from '@dashboard/components/MultilineText';
import Spinner from '@dashboard/components/Spinner';
import Button from '@dashboard/components/Button';
import useLicenseKeyQueries from '@dashboard/hooks/queries/useLicenseKeyQueries';
import CopyToClipboard from '@dashboard/components/CopyToClipboard';
import {
  ClipboardIcon,
  ArrowTopRightOnSquareIcon,
  EyeIcon,
  EnvelopeIcon,
  XMarkIcon,
  ArrowRightIcon,
  PencilSquareIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import PropertyList from '@dashboard/components/PropertyList';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import Tooltip from '@dashboard/components/Tooltip';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import renderFieldValue from '@dashboard/services/pageFieldTypes/renderFieldValue';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import getPaymentMethodLabel from '@dashboard/services/getPaymentMethodLabel';
import { PropertyListItem } from '@dashboard/components/PropertyList/PropertyList';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import { getTaxDisplayName } from '@dashboard/services/getTaxDisplayName';
import { useQueryClient } from '@tanstack/react-query';
import { chargeKey } from '@dashboard/lib/queryKeys';
import { useMutation } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useToast } from '@dashboard/context/ToastContext';
import FieldEditDialog from '@dashboard/components/FieldEditDialog';
import { getDataFieldTypeLabel } from '@dashboard/services/pageFieldTypes/dataFieldTypes';
import { useAdminGhosting } from '@dashboard/context/AdminGhostingContext/AdminGhostingContext';
import PaymentsBookingCancelDialog from '../components/PaymentsBookingCancelDialog';
import PaymentsBookingRefundDialog from '../components/PaymentsBookingRefundDialog';
import { confirm } from '@dashboard/lib/confirm';
import { formatDateTime } from '@/dashboard/lib/format-date';
import CardListItemElement from '@/dashboard/components/CardListItemElement';
import CardListItem from '@/dashboard/components/CardListItem';
import CardHeader from '@/dashboard/components/CardHeader';
import CardHeaderTitle from '@/dashboard/components/CardHeaderTitle';
import isAbandonedAbandonment from '@/dashboard/services/abandonment/isAbandonedAbandonment';
import getAbandonmentReminderText from '@/dashboard/lib/getAbandonmentReminderText';
import getEmailRecoveryStage from '@/dashboard/services/abandonment/getEmailRecoveryStage';
import Dash from '@/dashboard/components/Dash';

const PaymentsBookingsDetail = ({ payment }) => {
  const { seller, isAdminLoggedInAsSeller, token } = useAuth();
  const { setAdminGhostingSellerId } = useAdminGhosting();
  const { toggleLicenseKey } = useLicenseKeyQueries();
  const [isTogglingLicenseKey, setIsTogglingLicenseKey] = useState(false);
  const queryClient = useQueryClient();
  const { emitToast } = useToast();

  useEffect(() => {
    if (payment?.seller) {
      setAdminGhostingSellerId(payment.seller);
    }
  }, [payment?.seller, setAdminGhostingSellerId]);

  const { mutateAsync: updateFields } = useMutation({
    mutationFn: async values => {
      return await fetcher(`/sellers/${seller._id}/charges/${payment._id}`, token, {
        method: 'PUT',
        body: JSON.stringify({ fields: values }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chargeKey(payment._id) });

      emitToast({
        title: 'Fields updated',
      });
    },
  });

  const handleSendChargeConfirmationEmail = async () => {
    const confirmed = await confirm('Send a new confirmation email to the customer?', {
      confirm: 'Send',
    });

    if (confirmed) {
      sendChargeConfirmationEmail();
    }
  };

  const { mutateAsync: sendChargeConfirmationEmail, isLoading: isSendingChargeConfirmationEmail } =
    useMutation({
      mutationFn: async () => {
        return await fetcher(
          `/sellers/${seller._id}/charges/${payment._id}/send-confirmation-email`,
          token,
          {
            method: 'POST',
          },
        );
      },
      onSuccess: () => {
        emitToast({
          title: 'Confirmation email resent',
        });
      },
      onError: () => {
        emitToast({
          title: 'Failed to send confirmation email',
          type: 'error',
        });
      },
    });

  const { mutateAsync: downloadTicketPDF, isPending: isDownloadingTicketPDF } = useMutation({
    mutationFn: async () => {
      return await fetcher(`/sellers/${seller._id}/tickets/${payment._id}/pdf`, token, {
        method: 'get',
      });
    },
    onSuccess: data => {
      if (data.pdfUrl == null) {
        emitToast({
          title: 'Failed to download ticket PDF',
          type: 'error',
        });
        return;
      }

      const link = document.createElement('a');
      link.href = data.pdfUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onError: () => {
      emitToast({
        title: 'Failed to download ticket PDF',
        type: 'error',
      });
    },
  });

  /**
   * @param {'enable'|'disable'} action
   */
  async function handleToggleLicenseKey() {
    setIsTogglingLicenseKey(true);

    const action = payment.licenseKey.enabled ? 'disable' : 'enable';

    const { error } = await toggleLicenseKey(payment.licenseKey._id, action);

    if (error) {
      setIsTogglingLicenseKey(false);
      window.alert(`Failed to ${action} license key. Try again or contact support.`);
      return;
    }

    queryClient.invalidateQueries({ queryKey: chargeKey(payment._id) });

    setIsTogglingLicenseKey(false);
  }

  const { amountRefunded } = (payment.transactions ?? []).reduce(
    (acc, transaction) => {
      if (transaction.status === 'refunded' || transaction.status === 'partially_refunded') {
        acc.amountRefunded += transaction.amount;
      }

      return acc;
    },
    {
      amountRefunded: 0,
    },
  );

  const paymentDetails = {
    Event: payment.page?.name ? (
      <Link to={`/pages/${payment.page?._id}`} className="font-medium">
        {payment.page?.name}
      </Link>
    ) : (
      <Dash />
    ),
    'Total amount': getPriceLabel({
      amount: payment.amount,
      currency: payment.currency,
    }),
    'Amount due': getPriceLabel({
      amount: payment.amountDue,
      currency: payment.currency,
    }),
    'Paid amount': getPriceLabel({
      amount: payment.amountPaid,
      currency: payment.currency,
    }),
    ...(amountRefunded &&
      amountRefunded && {
        'Refunded amount': getPriceLabel({
          amount: amountRefunded,
          currency: payment.currency,
        }),
      }),
    ...(payment.fees?.length > 0
      ? Object.fromEntries(
          payment.fees.map(fee => [
            fee.name,
            `${getPriceLabel({
              amount: fee.amount,
              currency: payment.currency,
            })} ${fee.percentage ? `(${fee.percentage}%)` : ''}`,
          ]),
        )
      : {}),
    ...(payment.taxBreakdown && payment.taxBreakdown.length > 0
      ? Object.fromEntries(
          payment.taxBreakdown.map(tax => [
            `${getTaxDisplayName(tax.taxType)} (${tax.taxRateDetails?.country}${
              tax.taxRateDetails?.state ? `, ${tax.taxRateDetails.state}` : ''
            }${
              tax.taxRateDetails?.percentageDecimal
                ? ` ${tax.taxRateDetails.percentageDecimal}%`
                : ''
            })`,
            <>
              {getPriceLabel({
                amount: tax.taxAmount,
                currency: payment.currency,
              })}
            </>,
          ]),
        )
      : payment.taxAmount
        ? {
            'Tax amount': getPriceLabel({
              amount: payment.taxAmount,
              currency: payment.currency,
            }),
          }
        : {}),
    'Payment type': 'One-time',
    Created: formatDateTime(payment.createdAt),
  };

  const customerDetails = {
    'Customer name': (payment.customer && payment.customer.name) || 'Not provided',
    'Customer email':
      payment.customer && payment.customer.email ? (
        <Link to={`mailto:${payment.customer.email}`} target="_blank" variant="withIcon">
          <EnvelopeIcon className="h-4 w-4 mt-1" />

          {payment.customer.email}
        </Link>
      ) : (
        'Not provided'
      ),
  };

  const details = {
    'Booking ID': payment.orderId,
    'Payment method': payment.paymentMethod ? (
      getPaymentMethodLabel(payment.paymentMethod)
    ) : (
      <Dash />
    ),
    SKU: payment.sku || <Dash />,
    ...(payment.feeAmount && {
      'Checkout Page fee': formatPrice({
        amount: payment.feeAmount,
        currency: payment.currency,
      }),
    }),
    'Customer ID': (payment.customer && `${payment.customer._id}`) || <Dash />,
    'Payment ID': `${payment._id}`,
    'Stripe customer ID': payment.customer?.stripeCustomerId ? (
      <StripeLinkWrapper
        disabled={!payment.livemode}
        triggerClassName={!payment.livemode ? '!w-auto' : ''}
        tooltipLabel="Only for live payments"
      >
        <Link
          to={getStripeDashboardUrl(`/customers/${payment.customer.stripeCustomerId}`, {
            seller,
            isAdminLoggedInAsSeller,
          })}
          blank
          className={clsx({
            'pointer-events-none opacity-50 cursor-not-allowed': !payment.livemode,
          })}
          external
        >
          {payment.customer.stripeCustomerId}
        </Link>
      </StripeLinkWrapper>
    ) : (
      <Dash />
    ),
    'Stripe charge ID': payment.chargeId ? (
      <div>
        <StripeLinkWrapper
          disabled={!payment.livemode}
          triggerClassName={!payment.livemode ? '!w-auto' : ''}
          tooltipLabel="Only for live payments"
        >
          <Link
            to={getStripeDashboardUrl(`/charges/${payment.chargeId}`, {
              seller,
              isAdminLoggedInAsSeller,
            })}
            blank
            className={clsx({
              'pointer-events-none opacity-50 cursor-not-allowed': !payment.livemode,
            })}
            external
          >
            {payment.chargeId}
          </Link>
        </StripeLinkWrapper>
      </div>
    ) : (
      <Dash />
    ),
    Prefilled: payment.isPrefilled ? 'Is prefilled' : '—',
  };

  const queryParameters = payment.queryParameters
    ? Object.entries(payment.queryParameters).filter(([name]) => {
        return name !== 'previewKey' && name !== 'testing';
      })
    : [];

  const emailRecoveryStage = getEmailRecoveryStage(payment.queryParameters, payment.recovertedAt);

  const groupedTickets = groupTicketsByTicketGroup(payment.tickets);

  const hasPdf =
    payment.page._id === '681aac984e3f8ba880f636f8' ||
    payment.page._id === '667211f37afaf75caf59ffce' ||
    payment.page._id === '67ff336a0ea017badff86621' ||
    payment.page._id === '67e9ebab28eae4f818011f12' ||
    (payment?.createdAt && new Date(payment.createdAt) > new Date('2025-05-12T08:35:07.000Z'));

  return (
    <>
      <div className="flex flex-col lg:flex-row max-w-5xl items-start justify-center">
        <div className="order-2 lg:order-1 w-full lg:pr-8">
          {payment.orderStatus === 'canceled' && (
            <div className="px-4">
              <Callout variant="error" padded={false}>
                <Callout.Heading>Booking canceled</Callout.Heading>
                {payment.canceledAt != null && (
                  <Callout.Content>{formatDateTime(payment.canceledAt)}</Callout.Content>
                )}
                {payment.reason != null && <Callout.Content>{payment.reason}</Callout.Content>}
              </Callout>
            </div>
          )}

          {(payment.status === 'failed' || payment.status === 'canceled') && (
            <div className="px-4">
              <Callout variant="error" padded={false}>
                <Callout.Heading>Payment failed</Callout.Heading>
                <Callout.Content>{payment.paymentError}</Callout.Content>
              </Callout>
            </div>
          )}

          {payment.status === 'created' && (
            <div className="px-4">
              <Callout variant="warning" padded={false}>
                <Callout.Heading>Payment created</Callout.Heading>
                <Callout.Content>
                  The payment has been created but not yet completed.
                </Callout.Content>
              </Callout>
            </div>
          )}

          {payment.status === 'pending' && (
            <div className="px-4">
              <Callout variant="warning" padded={false}>
                <Callout.Heading>Payment pending</Callout.Heading>
                <Callout.Content>
                  Customer has completed the payment, but the payment is still pending.
                </Callout.Content>
              </Callout>
            </div>
          )}

          <Card className="mx-auto">
            <CardContent>
              <PropertyList.Title>Payment</PropertyList.Title>
              {Object.entries(paymentDetails).map(([key, value], index) => (
                <PropertyList.Item key={index}>
                  <PropertyList.ItemLabel>{key}</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>{value}</PropertyList.ItemValue>
                </PropertyList.Item>
              ))}
            </CardContent>
          </Card>

          {payment.isAbandoned === true && (
            <Card className="mx-auto">
              <CardContent>
                <PropertyList.Title>
                  Cart abandonment
                  <div className="mt-auto ml-auto">
                    <Link
                      to="/settings/cart-abandonment"
                      className="flex items-center gap-x-1 whitespace-nowrap text-sm"
                    >
                      Abandonment settings <ArrowRightIcon className="w-4 h-4" />
                    </Link>
                  </div>
                </PropertyList.Title>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Recovered</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    {payment.recoveredAt ? formatDateTime(payment.recoveredAt) : <Dash />}
                  </PropertyList.ItemValue>
                </PropertyList.Item>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Recovered by</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>{emailRecoveryStage ?? <Dash />}</PropertyList.ItemValue>
                </PropertyList.Item>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Reminder email</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    {getAbandonmentReminderText(seller, payment)}
                  </PropertyList.ItemValue>
                </PropertyList.Item>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Abandoned</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    {payment.createdAt ? formatDateTime(addHours(payment.createdAt, 1)) : <Dash />}
                  </PropertyList.ItemValue>
                </PropertyList.Item>
              </CardContent>
            </Card>
          )}

          {groupedTickets.length > 0 && (
            <Card className="mx-auto">
              <CardHeader>
                <CardHeaderTitle>Tickets</CardHeaderTitle>

                <div className="mb-2 pt-[2px] leading-tight">
                  <Link
                    to={`/orders/bookings/${payment._id}/tickets`}
                    className="flex items-center gap-x-1 whitespace-nowrap text-sm"
                  >
                    Manage tickets <ArrowRightIcon className="w-4 h-4" />
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                <CardListItem variant="header" className="!mt-0">
                  <CardListItemElement size="fill" className="!pl-0">
                    Name
                  </CardListItemElement>
                  <CardListItemElement size="xs">Qty</CardListItemElement>
                  <CardListItemElement size="medium">Price</CardListItemElement>
                  <CardListItemElement size="large">Bulk discount</CardListItemElement>
                  <CardListItemElement size="medium">Amount</CardListItemElement>
                </CardListItem>

                {groupedTickets.map((group, groupIndex) => (
                  <React.Fragment key={groupIndex}>
                    {groupedTickets.length > 1 && (
                      <CardListItem>
                        <CardListItemElement size="fill" className="!pl-0 font-bold">
                          {group.name}
                        </CardListItemElement>
                      </CardListItem>
                    )}

                    {group.tickets.map((ticket, index) => (
                      <CardListItem key={index}>
                        <CardListItemElement size="fill" className="!pl-0">
                          {ticket.name}
                        </CardListItemElement>
                        <CardListItemElement align="center" size="xs">
                          {ticket.quantity}
                        </CardListItemElement>
                        <CardListItemElement size="medium">
                          {ticket.originalPrice > 0
                            ? getPriceLabel({
                                amount: ticket.originalPrice,
                                currency: payment.currency,
                              })
                            : 'Free'}
                        </CardListItemElement>
                        <CardListItemElement size="large">
                          {ticket.discount?.amount > 0 ? (
                            getPriceLabel({
                              amount: ticket.discount?.amount,
                              currency: payment.currency,
                            })
                          ) : (
                            <Dash />
                          )}
                        </CardListItemElement>
                        <CardListItemElement size="medium">
                          {ticket.price > 0
                            ? getPriceLabel({
                                amount: ticket.price * ticket.quantity,
                                currency: payment.currency,
                              })
                            : 'Free'}
                        </CardListItemElement>
                      </CardListItem>
                    ))}
                  </React.Fragment>
                ))}
              </CardContent>
            </Card>
          )}

          {payment.customer && (
            <Card className="mx-auto">
              <CardContent>
                <PropertyList.Title>
                  Customer
                  <div className="mt-auto ml-auto">
                    <Link
                      to={`/customers/${payment.customer._id}`}
                      className="flex items-center gap-x-1 whitespace-nowrap text-sm"
                    >
                      View customer <ArrowRightIcon className="w-4 h-4" />
                    </Link>
                  </div>
                </PropertyList.Title>
                {Object.entries(customerDetails).map(([key, value], index) => (
                  <PropertyList.Item key={index}>
                    <PropertyList.ItemLabel>{key}</PropertyList.ItemLabel>
                    <PropertyList.ItemValue>{value}</PropertyList.ItemValue>
                  </PropertyList.Item>
                ))}
              </CardContent>
            </Card>
          )}

          {payment.discount && (
            <Card className="mx-auto">
              <CardContent>
                <PropertyList.Title>Bulk discount</PropertyList.Title>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Min / max quantity</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    {payment.discount.minQuantity} / {payment.discount.maxQuantity ?? <Dash />}
                  </PropertyList.ItemValue>
                </PropertyList.Item>

                {payment.discount.amountOff ? (
                  <PropertyListItem>
                    <PropertyList.ItemLabel>Amount off</PropertyList.ItemLabel>
                    <PropertyList.ItemValue>
                      -
                      {getPriceLabel({
                        amount: payment.discount.amountOff,
                        currency: payment.currency,
                      })}
                    </PropertyList.ItemValue>
                  </PropertyListItem>
                ) : (
                  <PropertyList.Item>
                    <PropertyList.ItemLabel>Percentage off</PropertyList.ItemLabel>
                    <PropertyList.ItemValue>{payment.discount.percentOff}%</PropertyList.ItemValue>
                  </PropertyList.Item>
                )}

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Discount amount</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    -
                    {getPriceLabel({
                      amount: payment.discount.amount,
                      currency: payment.currency,
                    })}
                  </PropertyList.ItemValue>
                </PropertyList.Item>
              </CardContent>
            </Card>
          )}

          {payment.coupon && (
            <Card className="mx-auto">
              <CardContent>
                <PropertyList.Title>
                  Coupon
                  <div className="mt-auto ml-auto">
                    <Link
                      to={`/coupons/${payment.coupon._id}`}
                      className="flex items-center gap-x-1 whitespace-nowrap text-sm"
                    >
                      View coupon <ArrowRightIcon className="w-4 h-4" />
                    </Link>
                  </div>
                </PropertyList.Title>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Label</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>{payment.coupon.label}</PropertyList.ItemValue>
                </PropertyList.Item>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Code</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    <Link to={`/coupons/${payment.coupon._id}`}>{payment.coupon.code}</Link>
                  </PropertyList.ItemValue>
                </PropertyList.Item>

                {payment.coupon.percentOff && (
                  <PropertyList.Item>
                    <PropertyList.ItemLabel>Discount percentage</PropertyList.ItemLabel>
                    <PropertyList.ItemValue>{payment.coupon.percentOff}%</PropertyList.ItemValue>
                  </PropertyList.Item>
                )}

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Discount amount</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    -
                    {getPriceLabel({
                      amount: payment.coupon.discount,
                      currency: payment.currency,
                    })}
                  </PropertyList.ItemValue>
                </PropertyList.Item>
              </CardContent>
            </Card>
          )}

          {payment.variants && payment.variants.length > 0 ? (
            <Card className="mx-auto">
              <CardContent>
                <PropertyList.Title>Variants</PropertyList.Title>

                {payment.variants.map(variant => {
                  if (variant.selectedOptionId) {
                    return (
                      <PropertyList.Item key={variant._id}>
                        <PropertyList.ItemLabel>{variant.name}</PropertyList.ItemLabel>

                        <PropertyList.ItemValue>
                          <PropertyList.ItemValue>
                            <div className="flex">
                              {!variant.additionalChargeAmount && (
                                <div className="whitespace-nowrap mr-1">
                                  {variant.selectedOptionQuantity || 1}
                                  <XMarkIcon className="w-3 h-3 mt-0.5 inline-block" />
                                </div>
                              )}

                              <span className="w-2/4">{variant.selectedOptionName}</span>

                              {variant.additionalChargeAmount && (
                                <>
                                  <div className="w-1/4 text-right">
                                    <span className="whitespace-nowrap mr-1">
                                      {variant.selectedOptionQuantity || 1}
                                      <XMarkIcon className="w-3 h-3 mt-0.5 inline-block" />
                                    </span>

                                    {variant.originalAdditionalChargeAmount > 0 &&
                                      variant.additionalChargeAmount !==
                                        variant.originalAdditionalChargeAmount && (
                                        <span className="line-through mr-1">
                                          {getPriceLabel({
                                            amount: variant.originalAdditionalChargeAmount,
                                            currency: payment.currency,
                                          })}
                                        </span>
                                      )}

                                    <span>
                                      {getPriceLabel({
                                        amount: variant.additionalChargeAmount,
                                        currency: payment.currency,
                                      })}
                                    </span>
                                  </div>

                                  <div className="w-1/4 text-right">
                                    Total:{' '}
                                    {getPriceLabel({
                                      amount:
                                        variant.additionalChargeAmount *
                                        (variant.selectedOptionQuantity || 1),
                                      currency: payment.currency,
                                    })}
                                  </div>
                                </>
                              )}
                            </div>
                          </PropertyList.ItemValue>

                          {variant.sku && (
                            <PropertyList.ItemValue>SKU: {variant.sku}</PropertyList.ItemValue>
                          )}
                        </PropertyList.ItemValue>
                      </PropertyList.Item>
                    );
                  }

                  if (variant.selectedOptions && variant.selectedOptions.length > 0) {
                    return variant.selectedOptions.map((option, index) => (
                      <PropertyListItem key={index}>
                        <PropertyList.ItemLabel>{variant.name}</PropertyList.ItemLabel>

                        <PropertyList.ItemValue>
                          <PropertyList.ItemValue>
                            <div className="flex">
                              {!option.additionalChargeAmount && (
                                <div className="whitespace-nowrap mr-1">
                                  {option.quantity}
                                  <XMarkIcon className="w-3 h-3 mt-0.5 inline-block" />
                                </div>
                              )}

                              <span className="w-2/4">{option.optionName}</span>

                              {option.additionalChargeAmount && (
                                <>
                                  <div className="w-1/4 text-right">
                                    <span className="whitespace-nowrap mr-1">
                                      {option.quantity}
                                      <XMarkIcon className="w-3 h-3 mt-0.5 inline-block" />
                                    </span>

                                    {option.originalAdditionalChargeAmount > 0 &&
                                      option.additionalChargeAmount !==
                                        option.originalAdditionalChargeAmount && (
                                        <span className="line-through mr-1">
                                          {getPriceLabel({
                                            amount: option.originalAdditionalChargeAmount,
                                            currency: payment.currency,
                                          })}
                                        </span>
                                      )}

                                    <span>
                                      {getPriceLabel({
                                        amount: option.additionalChargeAmount,
                                        currency: payment.currency,
                                      })}
                                    </span>
                                  </div>

                                  <div className="w-1/4 text-right">
                                    Total:{' '}
                                    {getPriceLabel({
                                      amount: option.additionalChargeAmount * option.quantity,
                                      currency: payment.currency,
                                    })}
                                  </div>
                                </>
                              )}
                            </div>
                          </PropertyList.ItemValue>

                          {option.sku && (
                            <PropertyList.ItemValue>{option.sku}</PropertyList.ItemValue>
                          )}
                        </PropertyList.ItemValue>
                      </PropertyListItem>
                    ));
                  }

                  return null;
                })}
              </CardContent>
            </Card>
          ) : (
            ''
          )}

          {payment.fields && payment.fields.length > 0 && (
            <Card className="mx-auto">
              <CardContent>
                <PropertyList.Title>
                  Fields
                  <div className="mt-auto ml-auto">
                    <FieldEditDialog
                      fields={payment.fields}
                      page={payment.page}
                      updateFields={updateFields}
                    />
                  </div>
                </PropertyList.Title>
                {payment.fields.map(field => (
                  <PropertyList.Item key={field._id}>
                    <PropertyList.ItemLabel>
                      {field.label}
                      <div className="text-gray-500 text-xs font-normal">
                        {getDataFieldTypeLabel(field.type)}
                      </div>
                    </PropertyList.ItemLabel>
                    <PropertyList.ItemValue>
                      <MultilineText>{renderFieldValue(field)}</MultilineText>
                    </PropertyList.ItemValue>
                  </PropertyList.Item>
                ))}
              </CardContent>
            </Card>
          )}

          {payment.licenseKey && payment.licenseKey.key && (
            <Card className="mx-auto">
              <CardContent>
                <PropertyList.Title>License key</PropertyList.Title>
                <PropertyList.Item>
                  <PropertyList.ItemLabel>Key</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    {payment.licenseKey.key}
                    <CopyToClipboard text={payment.licenseKey.key} className="ml-1">
                      <Button appearance="subtle">
                        <ClipboardIcon width="16px" />
                      </Button>
                    </CopyToClipboard>
                  </PropertyList.ItemValue>
                </PropertyList.Item>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Uses</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>{payment.licenseKey.uses}</PropertyList.ItemValue>
                </PropertyList.Item>

                <PropertyList.Item>
                  <PropertyList.ItemLabel>Status</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>
                    <PropertyList.ItemValue>
                      {payment.licenseKey.enabled ? 'Enabled' : 'Disabled'}
                    </PropertyList.ItemValue>

                    <PropertyList.ItemValue className="inline-flex items-center gap-x-2">
                      <Button
                        appearance="subtle"
                        onClick={handleToggleLicenseKey}
                        disabled={isTogglingLicenseKey}
                      >
                        {payment.licenseKey.enabled ? 'Disable' : 'Enable'}
                      </Button>

                      {isTogglingLicenseKey && <Spinner width="24px" height="24px" />}
                    </PropertyList.ItemValue>
                  </PropertyList.ItemValue>
                </PropertyList.Item>
              </CardContent>
            </Card>
          )}

          <Card className="mx-auto">
            <CardContent>
              <PropertyList.Title>Details</PropertyList.Title>
              {Object.entries(details).map(([key, value], index) => (
                <PropertyList.Item key={index}>
                  <PropertyList.ItemLabel>{key}</PropertyList.ItemLabel>
                  <PropertyList.ItemValue>{value}</PropertyList.ItemValue>
                </PropertyList.Item>
              ))}
            </CardContent>
          </Card>

          {!isEmpty(queryParameters) && (
            <Card className="mx-auto">
              <CardContent>
                <PropertyList.Title>Query parameters</PropertyList.Title>
                {queryParameters.map(([name, value], index) => (
                  <PropertyList.Item key={index}>
                    <PropertyList.ItemLabel>{name}</PropertyList.ItemLabel>
                    <PropertyList.ItemValue>{value}</PropertyList.ItemValue>
                  </PropertyList.Item>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <div
          className="lg:border-l lg:border-b-0 border-gray-100 lg:mt-4 lg:pl-8 ml-4 lg:ml-0 pb-4 flex flex-col sm:flex-row whitespace-nowrap
           lg:flex-col gap-x-4 gap-y-2 items-start order-1 lg:order-2 lg:w-[22rem]"
        >
          {payment.customer && (
            <Link
              to={`/customers/${payment.customer._id}`}
              variant="withIcon"
              className="w-full flex gap-x-2 items-center"
            >
              <EyeIcon className="h-4 w-4" /> View customer
            </Link>
          )}
          <FieldEditDialog
            fields={payment.fields}
            page={payment.page}
            updateFields={updateFields}
            trigger={
              <Link
                className={clsx(
                  'w-full flex gap-x-2 items-center font-medium text-gray-900 cursor-pointer',
                )}
                onClick={() => {}}
              >
                <>
                  <PencilSquareIcon className="w-4 h-4" />
                  Edit fields
                </>
              </Link>
            }
          />
          {hasPdf && (
            <div
              className={clsx(
                'w-full flex gap-x-2 items-center font-medium text-gray-900 cursor-pointer',
                {
                  'pointer-events-none opacity-50 cursor-not-allowed':
                    isAbandonedAbandonment(payment),
                },
              )}
              onClick={downloadTicketPDF}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Download ticket PDF
              {isDownloadingTicketPDF && <Spinner className="h-4 w-4" />}
            </div>
          )}

          <Link
            onClick={handleSendChargeConfirmationEmail}
            className={clsx(
              'w-full flex gap-x-2 items-center',
              isSendingChargeConfirmationEmail && 'opacity-50 cursor-not-allowed',
              {
                'pointer-events-none opacity-50': isAbandonedAbandonment(payment),
              },
            )}
          >
            <>
              <EnvelopeIcon className="w-4 h-4" />
              Resend confirmation
            </>
            {isSendingChargeConfirmationEmail && <Spinner width="24px" height="24px" />}
          </Link>

          <PaymentsBookingCancelDialog payment={payment}></PaymentsBookingCancelDialog>

          <PaymentsBookingRefundDialog payment={payment}></PaymentsBookingRefundDialog>

          <StripeLinkWrapper
            disabled={!payment.livemode || !payment.chargeId}
            tooltipLabel="Only for live payments"
            className="w-full"
          >
            <Link
              to={getStripeDashboardUrl(`/charges/${payment.chargeId}`, {
                seller,
                isAdminLoggedInAsSeller,
              })}
              blank
              className={clsx(`w-full flex gap-x-2 items-center`, {
                'pointer-events-none opacity-50 cursor-not-allowed':
                  !payment.livemode || !payment.chargeId,
              })}
            >
              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
              View in Stripe
            </Link>
          </StripeLinkWrapper>
        </div>
      </div>
    </>
  );
};

function StripeLinkWrapper({ disabled, tooltipLabel, ...props }) {
  if (disabled) {
    return (
      <Tooltip side="right" label={tooltipLabel} triggerClassName="w-full" {...props}>
        {props.children}
      </Tooltip>
    );
  } else {
    const { key, children } = props;
    return <React.Fragment key={key}>{children}</React.Fragment>;
  }
}

function groupTicketsByTicketGroup(tickets = []) {
  const groupedTickets = {};

  tickets.forEach(ticket => {
    if (!groupedTickets[ticket.ticketGroup]) {
      groupedTickets[ticket.ticketGroup] = {
        _id: ticket.ticketGroupId,
        name: ticket.ticketGroupName,
        tickets: [],
      };
    }

    groupedTickets[ticket.ticketGroup].tickets.push(ticket);
  });

  return Object.values(groupedTickets);
}

export default PaymentsBookingsDetail;
